import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { artistAnalyticsService, ArtistOverviewData, UserArtist } from '../../services/api/artist-analytics.service';
import UnitWhiteLabelAccountCard from '../Banking/UnitWhiteLabelAccountCard';
import UnitWhiteLabelApp from '../Banking/UnitWhiteLabelApp';
import { callUnitCustomerToken, useUnitJwt, getCustomerTokenKey, getCustomerTokenExpKey, getTokenKey, getExpKey, getApiTypeKey } from "hooks/useUnitJwt";
import { auth } from '../../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { apiFetch } from '../../services/api/client';
import { getAccountBalance, getUnitCoAccountDetails } from '../../services/api/user.service';
import { useArtist } from '../../contexts/ArtistContext';
import { useUser } from '../../contexts/UserContext';

interface OverviewSectionProps {
  dateRange?: { from: Date; to: Date };
}

interface OverviewBoxProps {
  title: string;
  subtitle: string;
  percentage: string;
  isPositive: boolean;
  streams?: number;
  error?: string;
}

const OverviewBox: React.FC<OverviewBoxProps> = ({ title, subtitle, percentage, isPositive, streams, error }) => {
  return (
    <div className="bg-[#1C1C1C] border border-[#BBBBBB] rounded-lg p-3 min-w-[280px] md:min-w-0 md:flex-1 flex-shrink-0 overview-box h-[225px]" style={{ borderWidth: '1px' }}>
      <div className="flex items-center justify-between h-full">
        <div className="flex-1 flex flex-col justify-center">
          <h3 className="text-sm-nova font-medium text-black uppercase">{title}</h3>
          <p className="text-sm-nova text-gray-400 mt-0.5">{subtitle}</p>
          {streams !== undefined && (
            <p className="text-base font-semibold text-white mt-0.5">
              {artistAnalyticsService.formatStreams(streams)}
            </p>
          )}
          <div className={`flex items-center gap-1 mt-1 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
            <span className="text-sm-nova">{percentage}</span>
          </div>
          {error && (
            <p className="text-xs text-red-400 mt-0.5">Error loading data</p>
          )}
        </div>
        <div className={`flex-shrink-0 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
          {isPositive ? <TrendingUp size={32} /> : <TrendingDown size={32} />}
        </div>
      </div>
    </div>
  );
};

interface AvailableBalanceTileProps {
  artistId?: string;
  isLoading?: boolean;
}

interface TotalFollowersTileProps {
  overviewData: ArtistOverviewData[];
  isLoading?: boolean;
}

interface YouTubeMusicViewsTileProps {
  artistId?: string;
  isLoading?: boolean;
}

interface SpotifyListenersTileProps {
  artistId?: string;
  isLoading?: boolean;
}

interface CustomerSupportTileProps {
  artistId?: string;
  isLoading?: boolean;
}

interface CustomerBalanceTileProps {
  artistId?: string;
  isLoading?: boolean;
}

interface TicketData {
  id: string;
  user_id: string;
  ticket_id: string;
  created_at: string;
  updated_at: string;
  description: string;
  ticket_url: string;
  name: string;
  email: string;
  subject: string;
  current_ticket_info: {
    id: number;
    status: string;
    created_at: string;
    updated_at: string;
  };
}

interface TicketResponse {
  data: TicketData[];
  count: number;
  user_id: string;
}

interface LatestTicketResponse {
  data: TicketData;
  artist_id: string;
  selection_reason: string;
  open_tickets_count: number;
  total_tickets_count: number;
}

// New interface for proper follower data
interface TotalFollowersData {
  totalCurrent: number;
  totalPrevious: number;
  percentage: string;
  isPositive: boolean;
}

export const AvailableBalanceTile: React.FC<AvailableBalanceTileProps> = ({ artistId, isLoading = false }) => {
  const [customerToken, setCustomerToken] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const customerTokenRef = useRef<string | null>(null);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [accountNotFound, setAccountNotFound] = useState(false); // Track if account doesn't exist
  const { userProfile } = useUser();
  const { currentArtistId } = useArtist();
  
  // Dashboard/analytics: may use valid customer token from storage on refresh; banking unchanged
  const isWhiteLabelRoute = window.location.pathname === '/dashboard' || window.location.pathname === '/analytics';

  useEffect(() => {
    // Defer until we have a user
    if (!currentUserId) {
      return;
    }

    // For dashboard/analytics: on mount or when IDs set, use valid customer token from storage so widget shows after refresh
    if (isWhiteLabelRoute && currentUserId && currentArtistId) {
      const storedToken = localStorage.getItem(getCustomerTokenKey(currentUserId));
      const storedExp = localStorage.getItem(getCustomerTokenExpKey(currentUserId));
      const buffer = 5 * 60 * 1000; // 5 min
      if (storedToken && storedToken !== 'undefined' && storedExp) {
        const exp = parseInt(storedExp, 10);
        if (exp > Date.now() + buffer && storedToken !== customerTokenRef.current) {
          customerTokenRef.current = storedToken;
          setCustomerToken(storedToken);
          setHasInitialized(true);
          setAccountNotFound(false);
        }
      }
    }

    // Function to check and update customer token
    const checkCustomerToken = () => {
      try {
        if (!currentUserId) {
          return false;
        }
        
        // For white label routes: also check storage (dashboard stores token after fetch)
        if (isWhiteLabelRoute) {
          const storedToken = localStorage.getItem(getCustomerTokenKey(currentUserId));
          const storedExp = localStorage.getItem(getCustomerTokenExpKey(currentUserId));
          const buffer = 5 * 60 * 1000;
          if (storedToken && storedToken !== 'undefined' && storedExp) {
            const exp = parseInt(storedExp, 10);
            if (exp > Date.now() + buffer && storedToken !== customerTokenRef.current) {
              customerTokenRef.current = storedToken;
              setCustomerToken(storedToken);
              setHasInitialized(true);
              return true;
            }
          }
          return false;
        }
        
        const CUSTOMER_TOKEN_KEY = getCustomerTokenKey(currentUserId);
        const JWT_TOKEN_KEY = getTokenKey(currentUserId);
        
        const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
        const jwtToken = localStorage.getItem(JWT_TOKEN_KEY);

        // Only proceed if we have both tokens and customer token is valid
        // Update if token exists and is different from current state or ref
        // This ensures UI updates when token becomes available after retries
        if (token && token !== 'undefined' && jwtToken) {
          // Always update if token is different from ref (more reliable than state comparison)
          if (token !== customerTokenRef.current) {
            customerTokenRef.current = token;
            setCustomerToken(token);
            setHasInitialized(true);
            return true;
          }
        }
        return false;
      } catch (_error) {
        return false;
      }
    };

    // Check immediately on mount/user change (only if not already initialized)
    if (!hasInitialized) {
      const foundToken = checkCustomerToken();

      if (!foundToken && currentUserId && userProfile?.id && currentArtistId) {
        // Check if account exists before calling customer token API
        const checkAccountAndGetToken = async () => {
          try {
            const JWT_TOKEN_KEY = getTokenKey(currentUserId);
            const currentJwtToken = localStorage.getItem(JWT_TOKEN_KEY);
            
            if (!currentJwtToken) {
              return; // No JWT token available
            }

            // SKIPPED: Account existence check - always proceed to get customer token
            callUnitCustomerToken(null, currentUserId).catch(() => null);
          } catch (accountCheckError: any) {
            // Handle account check errors
            const errorStatus = accountCheckError?.error?.status || accountCheckError?.status;
            const errorCode = accountCheckError?.error?.code || accountCheckError?.code;
            
            // If 404 or account doesn't exist, don't proceed
            if (errorStatus === 404 || errorCode === 'ACCOUNT_NOT_FOUND') {
              console.log('ℹ️ AvailableBalanceTile: Account not found, skipping customer token request');
            } else {
              // For other errors (403, 500, etc.), log but don't break the flow
              console.warn('⚠️ AvailableBalanceTile: Account check failed, proceeding with customer token request:', accountCheckError);
              callUnitCustomerToken(null, currentUserId).catch(() => null);
            }
          }
        };

        checkAccountAndGetToken();
      }
    }

    // Listen for customer token ready event (for white label routes - token from API, not localStorage)
    const handleCustomerTokenReady = (e: CustomEvent) => {
      if (isWhiteLabelRoute && currentUserId && e.detail?.userId === currentUserId) {
        const token = e.detail?.customerToken;
        if (token && token !== 'undefined' && token !== customerTokenRef.current) {
          console.log('[AvailableBalanceTile] ✅ Received fresh customer token from API (white label route)');
          customerTokenRef.current = token;
          setCustomerToken(token);
          setHasInitialized(true);
          setAccountNotFound(false); // Reset account not found state
        }
      }
    };

    // Listen for customer token error event (when account doesn't exist)
    const handleCustomerTokenError = (e: CustomEvent) => {
      if (isWhiteLabelRoute && currentUserId && e.detail?.userId === currentUserId) {
        const errorStatus = e.detail?.status;
        if (errorStatus === 404 || errorStatus === '404') {
          console.log('[AvailableBalanceTile] ❌ Account not found (404) - no customer token available');
          setAccountNotFound(true);
          setHasInitialized(true);
        }
      }
    };

    // Listen for storage changes (when localStorage is modified from other tabs/windows)
    // Only for non-white-label routes
    const handleStorageChange = (e: StorageEvent) => {
      if (!isWhiteLabelRoute && currentUserId && (e.key === getCustomerTokenKey(currentUserId) || e.key === getTokenKey(currentUserId))) {
        // Always check for token when storage changes, even if already initialized
        // This ensures UI updates when token becomes available after retries
        checkCustomerToken();
      }
    };

    // Listen for custom events (when localStorage is modified from same tab)
    // Only for non-white-label routes
    const handleCustomStorageChange = (e: CustomEvent) => {
      if (!isWhiteLabelRoute && currentUserId && (e.detail?.key === getCustomerTokenKey(currentUserId) || e.detail?.key === getTokenKey(currentUserId))) {
        // Always check for token when custom event fires, even if already initialized
        // This ensures UI updates when token becomes available
        const tokenUpdated = checkCustomerToken();
        if (!tokenUpdated && e.detail?.key === getCustomerTokenKey(currentUserId) && !hasInitialized && userProfile?.id && currentArtistId) {
          // If token key changed but we didn't find a valid token, check account and fetch if exists
          const checkAccountAndGetToken = async () => {
            try {
              const JWT_TOKEN_KEY = getTokenKey(currentUserId);
              const currentJwtToken = localStorage.getItem(JWT_TOKEN_KEY);
              
              if (!currentJwtToken) {
                return; // No JWT token available
              }

              // SKIPPED: Account existence check - always proceed to get customer token
              callUnitCustomerToken(null, currentUserId).catch(() => null);
            } catch (accountCheckError: any) {
              const errorStatus = accountCheckError?.error?.status || accountCheckError?.status;
              const errorCode = accountCheckError?.error?.code || accountCheckError?.code;
              
              if (errorStatus === 404 || errorCode === 'ACCOUNT_NOT_FOUND') {
                console.log('ℹ️ AvailableBalanceTile: Account not found, skipping customer token request');
              } else {
                console.warn('⚠️ AvailableBalanceTile: Account check failed, proceeding with customer token request:', accountCheckError);
                callUnitCustomerToken(null, currentUserId).catch(() => null);
              }
            }
          };

          checkAccountAndGetToken();
        }
      }
    };

    // Add event listeners
    if (isWhiteLabelRoute) {
      // For white label routes, listen for API token events and errors
      window.addEventListener('unitcoCustomerTokenReady', handleCustomerTokenReady as EventListener);
      window.addEventListener('unitcoCustomerTokenError', handleCustomerTokenError as EventListener);
    } else {
      // For non-white-label routes, listen for localStorage changes
      window.addEventListener('storage', handleStorageChange);
      window.addEventListener('localStorageChange', handleCustomStorageChange as EventListener);
    }

    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setLoadingTimeout(true);
      setHasInitialized(true); // Mark as initialized even on timeout
    }, 8000); // 8 second timeout (reduced from 10)

    // Add periodic check as fallback
    // This should run even if hasInitialized is true, to catch tokens that arrive late
    const checkInterval = 1000; // Check every 1s
    const intervalId = setInterval(() => {
      if (currentUserId) {
        // Always check for token, even if initialized (in case it just became available)
        checkCustomerToken();
      }
    }, checkInterval);

      return () => {
      if (isWhiteLabelRoute) {
        window.removeEventListener('unitcoCustomerTokenReady', handleCustomerTokenReady as EventListener);
        window.removeEventListener('unitcoCustomerTokenError', handleCustomerTokenError as EventListener);
      } else {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('localStorageChange', handleCustomStorageChange as EventListener);
      }
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [hasInitialized, currentUserId, customerToken, userProfile?.id, currentArtistId]); // Include dependencies
  
  // State to control when to start fetching JWT token
  const [shouldFetchJwt, setShouldFetchJwt] = useState(false);
  
  // Check if banking should be shown (only on dashboard/banking pages and if user has banking setup)
  const shouldShowBanking = window.location.pathname === '/dashboard' || window.location.pathname === '/banking' || window.location.pathname === '/analytics';
  
  // Start fetching JWT token when banking card becomes visible
  useEffect(() => {
    if (shouldShowBanking && !shouldFetchJwt && currentUserId) {
      const JWT_TOKEN_KEY = getTokenKey(currentUserId);
      const EXP_KEY = getExpKey(currentUserId);
      const CUSTOMER_TOKEN_KEY = getCustomerTokenKey(currentUserId);
      const API_TYPE_KEY = getApiTypeKey(currentUserId);
      
      // Check if existing tokens are expired before clearing
      const existingJwt = localStorage.getItem(JWT_TOKEN_KEY);
      const existingExp = localStorage.getItem(EXP_KEY);
      const existingCustomerToken = localStorage.getItem(CUSTOMER_TOKEN_KEY);
      
      if (existingJwt && existingExp && existingCustomerToken) {
        const expTime = parseInt(existingExp);
        const currentTime = Date.now();
        const buffer = 5 * 60 * 1000; // 5 minutes buffer
        
        if (expTime > currentTime + buffer) {
          // Don't clear tokens, just enable JWT fetching
          setShouldFetchJwt(true);
          return;
        } else {
          localStorage.removeItem(JWT_TOKEN_KEY);
          localStorage.removeItem(EXP_KEY);
          localStorage.removeItem(CUSTOMER_TOKEN_KEY);
          localStorage.removeItem(API_TYPE_KEY);
        }
      }
      
      setShouldFetchJwt(true);
    }
  }, [shouldShowBanking, shouldFetchJwt, currentUserId]);
  
  // Only fetch JWT token when shouldFetchJwt is true
  const { jwt: jwtToken, currentUserId: jwtCurrentUserId } = useUnitJwt(shouldFetchJwt);
  
  // Get current user ID from Firebase Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUserId(user.uid);
      } else {
        setCurrentUserId(null);
        setCustomerToken(null);
        setHasInitialized(false);
      }
    });
    return () => unsubscribe();
  }, []);
  
  // Initialize tokens from localStorage when user/visibility available
  // CRITICAL: For white label routes, don't read from localStorage
  useEffect(() => {
    if (!currentUserId) return;
    
    // For white label routes, don't read from localStorage - only use API responses
    if (isWhiteLabelRoute) {
      if (shouldShowBanking) {
        setShouldFetchJwt(true); // Enable JWT fetching
      }
      return;
    }
    
    const CUSTOMER_TOKEN_KEY = getCustomerTokenKey(currentUserId);
    const JWT_TOKEN_KEY = getTokenKey(currentUserId);
    const EXP_KEY = getExpKey(currentUserId);
    
    const existingToken = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    const existingJwt = localStorage.getItem(JWT_TOKEN_KEY);
    const existingExp = localStorage.getItem(EXP_KEY);
    
    // Check if tokens are valid and not expired
    if (existingToken && existingToken !== 'undefined' && existingJwt && existingExp) {
      const expTime = parseInt(existingExp);
      const currentTime = Date.now();
      const buffer = 5 * 60 * 1000; // 5 minutes buffer
      
      if (expTime > currentTime + buffer) {
        customerTokenRef.current = existingToken;
        setCustomerToken(existingToken);
        setShouldFetchJwt(true); // Enable JWT fetching for future use
      } else {
        localStorage.removeItem(JWT_TOKEN_KEY);
        localStorage.removeItem(EXP_KEY);
        localStorage.removeItem(CUSTOMER_TOKEN_KEY);
        localStorage.removeItem(getApiTypeKey(currentUserId));
        if (shouldShowBanking) {
          setShouldFetchJwt(true);
        }
      }
    } else if (shouldShowBanking) {
      setShouldFetchJwt(true);
    }
  }, [currentUserId, shouldShowBanking, isWhiteLabelRoute]);

  // Reset accountNotFound when artist changes
  useEffect(() => {
    setAccountNotFound(false);
  }, [currentArtistId]);

  // Watch for JWT token changes (removed forceUpdate to prevent infinite re-renders)
  useEffect(() => {
    if (jwtToken && currentUserId) {
      // CRITICAL: For white label routes, don't read from localStorage
      // Customer token will come via 'unitcoCustomerTokenReady' event
      if (isWhiteLabelRoute) {
        // For white label routes, customer token comes from API event, not localStorage
        return;
      }
      
      // Check for customer token whenever JWT becomes available (non-white-label routes only)
      const CUSTOMER_TOKEN_KEY = getCustomerTokenKey(currentUserId);
      const currentToken = localStorage.getItem(CUSTOMER_TOKEN_KEY);
      
      if (currentToken && currentToken !== 'undefined') {
        // Token is available, update state
        if (currentToken !== customerToken) {
          customerTokenRef.current = currentToken;
          setCustomerToken(currentToken);
          setHasInitialized(true);
        }
      } else if (!hasInitialized && jwtToken && !customerToken && userProfile?.id && currentArtistId) {
        // JWT is available but customer token is not, proceed to get token (account check skipped)
        const checkAccountAndToken = async () => {
          try {
            // SKIPPED: Account existence check - always proceed to get customer token
            // Check for token periodically
            const CUSTOMER_TOKEN_KEY = getCustomerTokenKey(currentUserId);
            const checkInterval = setInterval(() => {
              const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
              if (token && token !== 'undefined') {
                customerTokenRef.current = token;
                setCustomerToken(token);
                setHasInitialized(true);
                clearInterval(checkInterval);
              }
            }, 500); // Check every 500ms
            
            // Stop checking after 5 seconds
            setTimeout(() => {
              clearInterval(checkInterval);
              if (!hasInitialized) {
                setHasInitialized(true);
              }
            }, 5000);
          } catch (error) {
            // On error, stop checking
            setHasInitialized(true);
          }
        };
        
        checkAccountAndToken();
      }
    }
  }, [jwtToken, currentUserId, customerToken, hasInitialized]);

  // Check for customer token periodically (only if not already initialized)
  useEffect(() => {
    if (shouldShowBanking && jwtToken && !customerToken && !hasInitialized && currentUserId && userProfile?.id && currentArtistId) {
      // SKIPPED: Account existence check - always proceed to get customer token
      const checkAccountAndGetToken = async () => {
        try {
          // SKIPPED: Account existence check - always proceed to get customer token
          if (isWhiteLabelRoute) {
            // For white label routes, customer token comes from API event, not localStorage
            console.log('ℹ️ AvailableBalanceTile: Waiting for customer token from API (white label route)');
          } else {
            // For non-white-label routes, check localStorage
            const CUSTOMER_TOKEN_KEY = getCustomerTokenKey(currentUserId);
            const currentToken = localStorage.getItem(CUSTOMER_TOKEN_KEY);
            if (currentToken && currentToken !== 'undefined') {
              customerTokenRef.current = currentToken;
              setCustomerToken(currentToken);
              setHasInitialized(true);
            } else {
              // No token yet, will be fetched by useUnitJwt
              console.log('ℹ️ AvailableBalanceTile: Waiting for customer token from useUnitJwt');
            }
          }
        } catch (accountCheckError: any) {
          const errorStatus = accountCheckError?.error?.status || accountCheckError?.status;
          const errorCode = accountCheckError?.error?.code || accountCheckError?.code;
          
          if (errorStatus === 404 || errorCode === 'ACCOUNT_NOT_FOUND') {
            console.log('ℹ️ AvailableBalanceTile: Account not found');
            setHasInitialized(true); // Mark as initialized to stop checking
          } else {
            // For other errors, log but continue checking
            console.warn('⚠️ AvailableBalanceTile: Account check failed:', accountCheckError);
          }
        }
      };

      checkAccountAndGetToken();
    }
  }, [shouldShowBanking, jwtToken, customerToken, hasInitialized, currentUserId, userProfile?.id, currentArtistId]);
  
  // SKIPPED: Account existence check removed for overview white label widget
  // The widget will render based on token availability only
  
  // Utility function to trigger localStorage refresh (can be called from other components)
  const triggerLocalStorageRefresh = () => {
    if (currentUserId) {
      const event = new CustomEvent('localStorageChange', {
        detail: { key: getCustomerTokenKey(currentUserId), userId: currentUserId }
      });
      window.dispatchEvent(event);
    }
  };
  
  // Calculate dynamic balance: $150,000 + artist ID
  const calculateBalance = (artistId: string | undefined): number => {
    const baseAmount = 150000;
    if (!artistId) return baseAmount;
    
    // Convert artist ID to number (if it's numeric) or use hash if it's string
    const artistNumericId = parseInt(artistId) || 0;
    return baseAmount + artistNumericId;
  };

  // Only calculate balance if we have an artistId and not loading
  const shouldShowBalance = !isLoading && artistId;
  const dynamicBalance = shouldShowBalance ? calculateBalance(artistId) : 0;
  
  return (
    <div className="bg-white border border-[#BBBBBB] rounded-lg p-3 min-w-[280px] md:min-w-0 md:flex-1 flex-shrink-0 overview-box h-[225px]" style={{ borderWidth: '1px', overflow: 'hidden' }}>
      <div className={`flex overflow-x-auto overflow-y-hidden pt-4 ${customerToken ? 'items-center' : 'items-center justify-center'} h-full`}>
        <div className="text-center">
          {/* //But don't render the UnitWhiteLabelApp UI if customerToken is not null or empty only should load the UnitWhiteLabelAccountCard UI */}
          {/* I want to load UnitWhiteLabelApp but not UI why beacuse UnitWhiteLabelApp can get the customer token once we recived customer token then use that tojen 
          UnitWhiteLabelAccountCard UI */}
          {(() => {
            // Don't show banking if it shouldn't be shown
            if (!shouldShowBanking) {
              return (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className="text-gray-600 text-sm">Banking not available</p>
                    <p className="text-xs text-gray-400 mt-1">Please visit the banking page</p>
                  </div>
                </div>
              );
            }

            // SKIPPED: Account existence check - widget will render based on token availability only
            // Show loading state while waiting for JWT token
            if (!jwtToken) {
              if (loadingTimeout) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <p className="text-gray-600 text-sm">Banking connection failed</p>
                      <p className="text-xs text-gray-400 mt-1">Please visit the banking page to set up your account</p>
                      <Link 
                        to="/banking" 
                        className="mt-2 inline-block px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 transition-colors"
                      >
                        Go to Banking
                      </Link>
                    </div>
                  </div>
                );
              }
              
              return (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-gray-600 text-sm">Loading banking...</p>
                  </div>
                </div>
              );
            }
            
            // Account exists and JWT is available, check for customer token
            if (!customerToken && jwtToken) {
              // Check if account doesn't exist
              if (accountNotFound || loadingTimeout) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <p className="text-gray-600 text-sm">No Bank Accounts</p>
                      <p className="text-xs text-gray-400 mt-1">Please visit the banking page to create your account</p>
                      <Link 
                        to="/banking" 
                        className="mt-2 inline-block px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 transition-colors"
                      >
                        Go to Banking
                      </Link>
                    </div>
                  </div>
                );
              }
              
              return (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-gray-600 text-sm">Connecting to banking...</p>
                  </div>
                </div>
              );
            }
            
            // Customer token is available, render white label widget (accountExists check skipped)
            if (customerToken && customerToken !== 'undefined') {
              return (
                <div className="h-full w-full">
                  <UnitWhiteLabelAccountCard key={`account-${artistId}`} customerToken={customerToken}/>
                </div>
              );
            }

            // Fallback: No customer token yet, check if account doesn't exist
            if (accountNotFound || loadingTimeout) {
              return (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className="text-gray-600 text-sm">No Bank Accounts</p>
                    <p className="text-xs text-gray-400 mt-1">Please visit the banking page to create your account</p>
                    <Link 
                      to="/banking" 
                      className="mt-2 inline-block px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 transition-colors"
                    >
                      Go to Banking
                    </Link>
                  </div>
                </div>
              );
            }

            // Still loading, show loading state
            return (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-gray-600 text-sm">Connecting to banking...</p>
                </div>
              </div>
            );
          })()}

          {/* <h3 className="text-sm-nova font-medium text-black uppercase mb-2">Available Balance</h3>
          {shouldShowBalance ? (
            <p className="text-2xl font-bold text-black">
              ${dynamicBalance.toLocaleString('en-US', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })}
            </p>
          ) : (
            <div className="flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export const TotalFollowersTile: React.FC<TotalFollowersTileProps> = ({ overviewData, isLoading = false }) => {
  const { currentArtistId } = useArtist();
  const [totalFollowersData, setTotalFollowersData] = useState<TotalFollowersData>({
    totalCurrent: 0,
    totalPrevious: 0,
    percentage: '0.0%',
    isPositive: true
  });
  const [dataLoading, setDataLoading] = useState(true);

  // Fetch total followers data from the overview endpoint (all-time cumulative data)
  useEffect(() => {
    // Don't fetch if no artist is selected
    if (!currentArtistId) {
      setDataLoading(false);
      return;
    }

    const fetchTotalFollowers = async () => {
      try {
        setDataLoading(true);
        
        // Use overview endpoint to get latest cumulative follower/subscriber counts
        // Pass currentArtistId to ensure header is attached
        const overviewResponse = await artistAnalyticsService.getArtistOverview(currentArtistId.toString());
        
        // Filter for followers and subscribers only, sum up current values
        let totalCurrent = 0;
        let totalPrevious = 0;
        
        overviewResponse.data.forEach(platformData => {
          // Check if this platform has followers or subscribers metrics
          if (platformData.allMetrics && Array.isArray(platformData.allMetrics)) {
            platformData.allMetrics.forEach((metric: any) => {
              if (metric.type === 'followers' || metric.type === 'subscribers') {
                const currentValue = metric.value || 0;
                // weeklyDiff is the change from previous to current, so previous = current - weeklyDiff
                const previousValue = currentValue - metric.weeklyDiff;
                
                totalCurrent += currentValue;
                totalPrevious += previousValue;
              }
            });
          } else {
            // Fallback to old format if allMetrics not available
            if (platformData.subtitle && 
                (platformData.subtitle.includes('FOLLOWERS') || 
                 platformData.subtitle.includes('SUBSCRIBERS'))) {
              
              totalCurrent += platformData.currentStreams || 0;
              totalPrevious += platformData.previousStreams || 0;
            }
          }
        });
        
        // Calculate percentage change
        let percentage = '0.0%';
        let isPositive = true;
        
        if (totalPrevious > 0) {
          const change = totalCurrent - totalPrevious;
          const percentChange = (change / totalPrevious) * 100;
          isPositive = percentChange >= 0;
          percentage = `${isPositive ? '+' : ''}${Math.abs(percentChange).toFixed(1)}%`;
        } else if (totalCurrent > 0) {
          percentage = '+100.0%';
          isPositive = true;
        }
        
        setTotalFollowersData({
          totalCurrent,
          totalPrevious,
          percentage,
          isPositive
        });
        
      } catch (_error) {
        return;
      } finally {
        setDataLoading(false);
      }
    };

    if (!isLoading) {
      fetchTotalFollowers();
    }
  }, [isLoading, currentArtistId]); // Wait for currentArtistId

  const { totalCurrent, percentage, isPositive } = totalFollowersData;
  
  return (
    <div className="bg-white border border-[#BBBBBB] rounded-lg p-3 min-w-[280px] md:min-w-0 md:flex-1 flex-shrink-0 overview-box h-[225px]" style={{ borderWidth: '1px' }}>
      <div className="flex items-center justify-between h-full">
        <div className="flex-1 flex flex-col justify-center">
          <h3 className="text-sm-nova font-semibold text-black uppercase">Total Followers</h3>
          <p className="text-sm-nova text-gray-700 mt-0.5">FOLLOWERS + SUBSCRIBERS</p>
          {isLoading || dataLoading ? (
            <div className="flex items-center mt-1">
              <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm-nova text-gray-400 ml-2">Loading...</span>
            </div>
          ) : (
            <>
              <p className="text-base font-semibold text-black mt-0.5">
                {artistAnalyticsService.formatStreams(totalCurrent)}
              </p>
              <div className={`flex items-center gap-1 mt-1 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                <span className="text-sm-nova">{percentage}</span>
              </div>
            </>
          )}
        </div>
        <div className={`flex-shrink-0 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
          {isLoading || dataLoading ? (
            <div className="w-8 h-8 bg-gray-600 rounded animate-pulse"></div>
          ) : (
            isPositive ? <TrendingUp size={32} /> : <TrendingDown size={32} />
          )}
        </div>
      </div>
    </div>
  );
};

export const YouTubeMusicViewsTile: React.FC<YouTubeMusicViewsTileProps> = ({ artistId, isLoading = false }) => {
  const { currentArtistId } = useArtist();
  const [youtubeData, setYoutubeData] = useState({
    currentViews: 0,
    previousViews: 0,
    percentage: '0.0%',
    isPositive: true
  });
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    // Use currentArtistId from context if artistId prop is not provided
    const effectiveArtistId = artistId || currentArtistId?.toString();
    
    // Don't fetch if no artist is available
    if (!effectiveArtistId) {
      setDataLoading(false);
      return;
    }

    const fetchYouTubeData = async () => {
      try {
        setDataLoading(true);
        
        // First try to get YouTube data from the overview endpoint (for consistency with other overview tiles)
        let overviewYoutubeData = null;
        try {
          const overviewResponse = await artistAnalyticsService.getArtistOverview(effectiveArtistId);
          if(overviewResponse.artist.imageUrl) {
            //console.log('overviewResponse.artist.imageUrl', overviewResponse.artist.imageUrl);
            localStorage.setItem('artistImageUrl', overviewResponse.artist.imageUrl);
            
            // Dispatch custom event to notify other components
            window.dispatchEvent(new CustomEvent('localStorageChange', {
              detail: {
                key: 'artistImageUrl',
                value: overviewResponse.artist.imageUrl
              }
            }));
          }
          // Find YouTube Music platform data
          overviewYoutubeData = overviewResponse.data.find(platformData => 
            platformData.platform.toLowerCase() === 'youtube_music'
          );
          
          // If YouTube Music has views metric, use that; otherwise use the primary metric
          if (overviewYoutubeData && overviewYoutubeData.allMetrics && Array.isArray(overviewYoutubeData.allMetrics)) {
            const viewsMetric = overviewYoutubeData.allMetrics.find((metric: any) => 
              metric.type === 'views'
            );
            
            if (viewsMetric) {
              // Use views data if available
              const currentViews = viewsMetric.value;
              const previousViews = currentViews - viewsMetric.weeklyDiff;
              
              // Calculate percentage change
              let percentage = '0.0%';
              let isPositive = true;
              
              if (previousViews > 0) {
                const change = viewsMetric.weeklyDiff;
                const percentChange = (change / previousViews) * 100;
                isPositive = percentChange >= 0;
                percentage = `${isPositive ? '+' : ''}${Math.abs(percentChange).toFixed(1)}%`;
              } else if (currentViews > 0) {
                percentage = '+100.0%';
                isPositive = true;
              }
              
              setYoutubeData({
                currentViews,
                previousViews,
                percentage,
                isPositive
              });
              
              return; // Exit early since we have the data
            }
          }
        } catch (overviewError) {
          // Fall back to monthly data if overview request fails
        }

        // Fallback to monthly views data
        const response = await artistAnalyticsService.getYouTubeViewsData(effectiveArtistId, 6);
        
        // Use the totalValue directly (like the original YouTube Music Views component does)
        let currentViews = response.data.totalValue || 0;
        let previousViews = 0;
        
        // Calculate previous period views from table data for percentage calculation
        if (response.data && response.data.table && response.data.chart.xAxisData) {
          const youtubeData = response.data.table.find(row => row.platform === 'YouTube Music');
          if (youtubeData && response.data.chart.xAxisData.length >= 2) {
            const months = response.data.chart.xAxisData;
            const firstMonth = youtubeData.monthlyData[months[0]] || 0;
            const lastMonth = youtubeData.monthlyData[months[months.length - 1]] || 0;
            
            // Use first month as previous value for comparison
            previousViews = firstMonth;
            // Use last month as current value for more accurate comparison
            if (lastMonth > 0) {
              currentViews = lastMonth;
            }
          }
        }
        
        // Calculate percentage change
        let percentage = '0.0%';
        let isPositive = true;
        
        if (previousViews > 0) {
          const change = currentViews - previousViews;
          const percentChange = (change / previousViews) * 100;
          isPositive = percentChange >= 0;
          percentage = `${isPositive ? '+' : ''}${Math.abs(percentChange).toFixed(1)}%`;
        } else if (currentViews > 0) {
          percentage = '+100.0%';
          isPositive = true;
        }
        
        setYoutubeData({
          currentViews,
          previousViews,
          percentage,
          isPositive
        });
        
      } catch (_error) {
        return;
      } finally {
        setDataLoading(false);
      }
    };

    if (!isLoading && effectiveArtistId) {
      fetchYouTubeData();
    }
  }, [artistId, isLoading, currentArtistId]); // Wait for currentArtistId

  const { currentViews, percentage, isPositive } = youtubeData;
  
  // Use the same formatting function as the original YouTube Music Views component
  const formatNumber = (num: number): string => {
    if (num >= 1000000000) {
      // Billions - show with 1 decimal place if under 100B, no decimals if over
      const billions = num / 1000000000;
      if (billions >= 100) {
        return `${Math.round(billions)}B`;
      }
      return `${billions.toFixed(1)}B`;
    } else if (num >= 1000000) {
      // Millions - show with 1 decimal place if under 100M, no decimals if over
      const millions = num / 1000000;
      if (millions >= 100) {
        return `${Math.round(millions)}M`;
      }
      return `${millions.toFixed(1)}M`;
    } else if (num >= 1000) {
      // Thousands - show with 1 decimal place if under 100K, no decimals if over
      const thousands = num / 1000;
      if (thousands >= 100) {
        return `${Math.round(thousands)}K`;
      }
      return `${thousands.toFixed(1)}K`;
    }
    return num.toLocaleString();
  };
  
  return (
    <div className="bg-white border border-[#BBBBBB] rounded-lg p-3 min-w-[280px] md:min-w-0 md:flex-1 flex-shrink-0 overview-box h-[225px]" style={{ borderWidth: '1px' }}>
      <div className="flex items-center justify-between h-full">
        <div className="flex-1 flex flex-col justify-center">
          <h3 className="text-sm-nova font-semibold text-black uppercase">YouTube Music Views</h3>
          <p className="text-sm-nova text-gray-700 mt-0.5">TOTAL VIEWS</p>
          {isLoading || dataLoading ? (
            <div className="flex items-center mt-1">
              <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm-nova text-gray-400 ml-2">Loading...</span>
            </div>
          ) : (
            <>
              <p className="text-base font-semibold text-black mt-0.5">
                {formatNumber(currentViews)}
              </p>
              <div className={`flex items-center gap-1 mt-1 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                <span className="text-sm-nova">{percentage}</span>
              </div>
            </>
          )}
        </div>
        <div className={`flex-shrink-0 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
          {isLoading || dataLoading ? (
            <div className="w-8 h-8 bg-gray-600 rounded animate-pulse"></div>
          ) : (
            isPositive ? <TrendingUp size={32} /> : <TrendingDown size={32} />
          )}
        </div>
      </div>
    </div>
  );
};

export const SpotifyListenersTile: React.FC<SpotifyListenersTileProps> = ({ artistId, isLoading = false }) => {
  const { currentArtistId } = useArtist();
  const [spotifyData, setSpotifyData] = useState({
    currentListeners: 0,
    previousListeners: 0,
    percentage: '0.0%',
    isPositive: true
  });
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    // Use currentArtistId from context if artistId prop is not provided
    const effectiveArtistId = artistId || currentArtistId?.toString();
    
    // Don't fetch if no artist is available
    if (!effectiveArtistId) {
      setDataLoading(false);
      return;
    }

    const fetchSpotifyData = async () => {
      try {
        setDataLoading(true);
        
        // First try to get Spotify data from the overview endpoint (for consistency with other overview tiles)
        let overviewSpotifyData = null;
        try {
          const overviewResponse = await artistAnalyticsService.getArtistOverview(effectiveArtistId);
          
          // Find Spotify platform data
          overviewSpotifyData = overviewResponse.data.find(platformData => 
            platformData.platform.toLowerCase() === 'spotify'
          );

          // If Spotify has listeners metric, use that; otherwise use the primary metric
          if (overviewSpotifyData && overviewSpotifyData.allMetrics && Array.isArray(overviewSpotifyData.allMetrics)) {
            const listenersMetric = overviewSpotifyData.allMetrics.find((metric: any) => 
              metric.type === 'listeners'
            );
            
            if (listenersMetric) {
              // Use listeners data if available
              const currentListeners = listenersMetric.value;
              const previousListeners = currentListeners - listenersMetric.weeklyDiff;
              
              // Calculate percentage change
              let percentage = '0.0%';
              let isPositive = true;
              
              if (previousListeners > 0) {
                const change = listenersMetric.weeklyDiff;
                const percentChange = (change / previousListeners) * 100;
                isPositive = percentChange >= 0;
                percentage = `${isPositive ? '+' : ''}${Math.abs(percentChange).toFixed(1)}%`;
              } else if (currentListeners > 0) {
                percentage = '+100.0%';
                isPositive = true;
              }
              
              setSpotifyData({
                currentListeners,
                previousListeners,
                percentage,
                isPositive
              });
              return; // Exit early since we have the data
            }
          }
        } catch (overviewError) {
          // Fall back to monthly data if overview call fails
        }

        // Fallback to monthly listeners data
        const response = await artistAnalyticsService.getMonthlyListenersData(effectiveArtistId, 6);
      
        // Calculate total current Spotify listeners from all monthly data
        let currentListeners = 0;
        let previousListeners = 0;
        
        if (response.data && response.data.table && response.data.chart.xAxisData) {
          const spotifyData = response.data.table.find(row => row.platform === 'Spotify');
          if (spotifyData && response.data.chart.xAxisData.length >= 2) {
            const months = response.data.chart.xAxisData;
            
            // Get the most recent month's listener count as current total
            // Note: Monthly listeners = rolling 28-day unique listeners (this IS the total)
            const lastMonth = months[months.length - 1];
            const firstMonth = months[0];
            
            currentListeners = spotifyData.monthlyData[lastMonth] || 0;
            previousListeners = spotifyData.monthlyData[firstMonth] || 0;
          }
        }
        
        // Calculate percentage change
        let percentage = '0.0%';
        let isPositive = true;
        
        if (previousListeners > 0) {
          const change = currentListeners - previousListeners;
          const percentChange = (change / previousListeners) * 100;
          isPositive = percentChange >= 0;
          percentage = `${isPositive ? '+' : ''}${Math.abs(percentChange).toFixed(1)}%`;
        } else if (currentListeners > 0) {
          percentage = '+100.0%';
          isPositive = true;
        }
        
        setSpotifyData({
          currentListeners,
          previousListeners,
          percentage,
          isPositive
        });
        
      } catch (_error) {
        return;
      } finally {
        setDataLoading(false);
      }
    };

    if (!isLoading && effectiveArtistId) {
      fetchSpotifyData();
    }
  }, [artistId, isLoading, currentArtistId]); // Wait for currentArtistId

  const { currentListeners, percentage, isPositive } = spotifyData;
  
  // Use the same formatting function as YouTube Music Views
  const formatNumber = (num: number): string => {
    if (num >= 1000000000) {
      const billions = num / 1000000000;
      if (billions >= 100) {
        return `${Math.round(billions)}B`;
      }
      return `${billions.toFixed(1)}B`;
    } else if (num >= 1000000) {
      const millions = num / 1000000;
      if (millions >= 100) {
        return `${Math.round(millions)}M`;
      }
      return `${millions.toFixed(1)}M`;
    } else if (num >= 1000) {
      const thousands = num / 1000;
      if (thousands >= 100) {
        return `${Math.round(thousands)}K`;
      }
      return `${thousands.toFixed(1)}K`;
    }
    return num.toLocaleString();
  };
  
  return (
    <div className="bg-white border border-[#BBBBBB] rounded-lg p-3 min-w-[280px] md:min-w-0 md:flex-1 flex-shrink-0 overview-box h-[225px]" style={{ borderWidth: '1px' }}>
      <div className="flex items-center justify-between h-full">
        <div className="flex-1 flex flex-col justify-center">
          <h3 className="text-sm-nova font-semibold text-black uppercase">Spotify Listeners</h3>
          <p className="text-sm-nova text-gray-700 mt-0.5">TOTAL LISTENERS</p>
          {isLoading || dataLoading ? (
            <div className="flex items-center mt-1">
              <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm-nova text-gray-400 ml-2">Loading...</span>
            </div>
          ) : (
            <>
              <p className="text-base font-semibold text-black mt-0.5">
                {formatNumber(currentListeners)}
              </p>
              <div className={`flex items-center gap-1 mt-1 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                <span className="text-sm-nova">{percentage}</span>
              </div>
            </>
          )}
        </div>
        <div className={`flex-shrink-0 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
          {isLoading || dataLoading ? (
            <div className="w-8 h-8 bg-gray-600 rounded animate-pulse"></div>
          ) : (
            isPositive ? <TrendingUp size={32} /> : <TrendingDown size={32} />
          )}
        </div>
      </div>
    </div>
  );
};

const CustomerBalanceTile: React.FC<CustomerBalanceTileProps> = ({ artistId, isLoading = false }) => {
  const { currentArtistId, loadingArtists } = useArtist();
  const [accountData, setAccountData] = useState<{
    accountName: string;
    accountType: string;
    accountNumber: string;
    balance: number;
  } | null>(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [hasZeroAccounts, setHasZeroAccounts] = useState(false);

  // Fetch account data
  useEffect(() => {
    const fetchAccountData = async () => {
      // Don't fetch if loading, artists are loading, or no artist selected
      if (isLoading || loadingArtists || !currentArtistId) {
        setDataLoading(false);
        setHasZeroAccounts(false);
        return;
      }
      
      // Verify localStorage has currentArtistId before making API calls
      const storedArtistId = localStorage.getItem('currentArtistId');
      if (!storedArtistId || storedArtistId !== currentArtistId.toString()) {
        // Wait a bit for ArtistContext to sync localStorage
        await new Promise(resolve => setTimeout(resolve, 100));
        const retryStoredArtistId = localStorage.getItem('currentArtistId');
        if (!retryStoredArtistId || retryStoredArtistId !== currentArtistId.toString()) {
          console.warn('[CustomerBalanceTile] currentArtistId not synced to localStorage yet, skipping API call');
          setDataLoading(false);
          return;
        }
      }

      try {
        setDataLoading(true);
        
        // Get user ID from sessionStorage
        const userId = sessionStorage.getItem('userid');
        if (!userId) {
          setDataLoading(false);
          return;
        }
        
        const userIdNum = parseInt(userId, 10);
        if (isNaN(userIdNum)) {
          setDataLoading(false);
          return;
        }
        
        // Fetch account balance data (X-Artist-Id header will be added automatically by apiFetch)
        const balanceData = await getAccountBalance(userIdNum);
        
        // Check if there are 0 accounts
        if (!balanceData.accounts || balanceData.accounts.length === 0) {
          setHasZeroAccounts(true);
          setAccountData(null);
          return;
        }
        
        setHasZeroAccounts(false);
        
        // Get artist name from response, use first one or default to 'Account'
        const accountName = balanceData.artist_name && balanceData.artist_name.length > 0 
          ? balanceData.artist_name[0] 
          : 'Account';
        
        // Get first account if available
        const account = balanceData.accounts[0];
        if (account) {
          // Extract last 4 digits of account number
          const accountNumber = account.account_number || '';
          const lastFour = accountNumber.length >= 4 
            ? accountNumber.slice(-4) 
            : accountNumber;
          
          // Use available_balance from response, or fallback to account balance
          const balance = balanceData.available_balance || parseFloat(account.balance) || 0;
          
          setAccountData({
            accountName: accountName,
            accountType: account.account_type === 'account' ? 'Checking' : account.account_type || 'Checking',
            accountNumber: lastFour,
            balance: balance
          });
        } else {
          setAccountData(null);
        }
      } catch (error: any) {
        // Handle missing artist ID errors specifically
        if (error?.isMissingArtistId || error?.message?.includes('Missing required header') || error?.message?.includes('x-artist-id')) {
          console.warn('[CustomerBalanceTile] Missing artist ID header, skipping account balance fetch');
        } else {
          console.error('[CustomerBalanceTile] Error fetching account balance:', error);
        }
        setAccountData(null);
        setHasZeroAccounts(false);
      } finally {
        setDataLoading(false);
      }
    };

    fetchAccountData();
  }, [isLoading, currentArtistId, loadingArtists]); // Refetch when artist changes or artists finish loading

  const formatBalance = (amount: number): { dollars: string; cents: string; isAbbreviated: boolean } => {
    // Check if amount exceeds 4 digits (9999)
    if (amount >= 10000) {
      let abbreviated: string;
      let suffix: string;
      
      if (amount >= 1000000000) {
        // Billions
        const billions = amount / 1000000000;
        abbreviated = billions.toFixed(2);
        suffix = 'B';
      } else if (amount >= 1000000) {
        // Millions
        const millions = amount / 1000000;
        abbreviated = millions.toFixed(2);
        suffix = 'M';
      } else {
        // Thousands
        const thousands = amount / 1000;
        abbreviated = thousands.toFixed(2);
        suffix = 'K';
      }
      
      const parts = abbreviated.split('.');
      return {
        dollars: parts[0] + suffix,
        cents: parts[1],
        isAbbreviated: true
      };
    }
    
    // Format in US number format with commas for thousands (for amounts less than 10K)
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    
    const parts = formatted.split('.');
    return {
      dollars: parts[0],
      cents: parts[1],
      isAbbreviated: false
    };
  };

  const balance = accountData ? formatBalance(accountData.balance) : { dollars: '0', cents: '00', isAbbreviated: false };

  return (
    <div className="bg-white border border-[#E0E0E0] rounded-lg p-4 min-w-[280px] md:min-w-0 md:flex-1 flex-shrink-0 overview-box h-[225px]" style={{ borderWidth: '1px' }}>
      <div className="flex flex-col h-full">
        {isLoading || dataLoading ? (
          // Show only loading state while fetching data
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center">
              <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mb-2"></div>
              <span className="text-sm text-gray-400">Loading...</span>
            </div>
          </div>
        ) : hasZeroAccounts ? (
          // Show only the message when there are 0 accounts
          <div className="flex flex-col items-center justify-center h-full text-center">
            <p className="text-sm font-semibold text-gray-700 mb-2">No bank account found</p>
            <p className="text-xs text-gray-500 mb-3">Please visit the banking page to create your account</p>
            <Link 
              to="/banking" 
              className="inline-block px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 transition-colors"
            >
              Go to Banking
            </Link>
          </div>
        ) : (
          <>
            {/* Icon and Account Name Row */}
            <div className="flex items-start gap-3 mb-4">
              {/* Circular Icon - Two horizontal arrows pointing to dollar sign */}
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Left arrow pointing right */}
                  <path d="M3 12H9M9 12L6 9M9 12L6 15" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  {/* Dollar sign in center */}
                  <path d="M12 6V18M12 6C13.1046 6 14 6.89543 14 8C14 9.10457 13.1046 10 12 10M12 6C10.8954 6 10 6.89543 10 8C10 9.10457 10.8954 10 12 10M12 14C13.1046 14 14 14.8954 14 16C14 17.1046 13.1046 18 12 18M12 14C10.8954 14 10 14.8954 10 16C10 17.1046 10.8954 18 12 18" stroke="black" strokeWidth="2" strokeLinecap="round"/>
                  {/* Right arrow pointing left */}
                  <path d="M21 12H15M15 12L18 9M15 12L18 15" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              
              {/* Account Name and Details */}
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-black leading-tight">
                  {accountData?.accountName || 'No Account'}
                </p>
                {accountData && (
                  <p className="text-xs text-gray-600 mt-1">
                    {accountData.accountType} · {accountData.accountNumber}
                  </p>
                )}
              </div>
            </div>

            {/* Available Balance Section */}
            <div className="mt-auto pt-4">
              <div className="mb-3">
                <p className="text-base font-bold text-gray-700 leading-tight">Available</p>
                <p className="text-base font-bold text-gray-700 leading-tight">Balance</p>
              </div>
              {accountData ? (
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold text-black leading-none">$</span>
                  <span className="text-5xl font-bold text-black ml-1 leading-none">{balance.dollars}</span>
                  {!balance.isAbbreviated && (
                    <span className="text-3xl font-bold text-black leading-none" style={{ verticalAlign: 'super', fontSize: '0.55em', lineHeight: '1' }}>.{balance.cents}</span>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <p className="text-sm text-gray-500 mb-3">No balance available</p>
                  <Link 
                    to="/banking" 
                    className="inline-block px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 transition-colors"
                  >
                    Go to Banking
                  </Link>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const CustomerSupportTile: React.FC<CustomerSupportTileProps> = ({ artistId, isLoading = false }) => {
  const { currentArtistId } = useArtist();
  const [tickets, setTickets] = useState<TicketData[]>([]);
  const [latestTicket, setLatestTicket] = useState<TicketData | null>(null);
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [dataLoading, setDataLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const itemsPerPage = 10;

  // Fetch latest ticket for initial display
  useEffect(() => {
    const fetchLatestTicket = async () => {
      if (!currentArtistId || isLoading) {
        setDataLoading(false);
        return;
      }

      try {
        setDataLoading(true);
        const response = await apiFetch('/zendesk/ticket-response/latest', {
          method: 'POST',
          body: JSON.stringify({ artistId: currentArtistId }),
        }) as LatestTicketResponse;

        if (response.data) {
          setLatestTicket(response.data);
          setOpenTicketsCount(response.open_tickets_count || 0);
        }
      } catch (_error) {
        setLatestTicket(null);
        setOpenTicketsCount(0);
      } finally {
        setDataLoading(false);
      }
    };

    fetchLatestTicket();
  }, [currentArtistId, isLoading]);


  // Use latestTicket for display (from latest endpoint)
  const displayTicket = latestTicket;

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const formatDateMMDDYYYY = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string): string => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'open') return 'text-yellow-700 bg-yellow-100 border border-yellow-200';
    if (statusLower === 'solved' || statusLower === 'closed') return 'text-green-700 bg-green-100 border border-green-200';
    if (statusLower === 'pending') return 'text-orange-700 bg-orange-100 border border-orange-200';
    return 'text-gray-700 bg-gray-100 border border-gray-200';
  };

  // Determine what to show
  const hasOpenTickets = openTicketsCount > 0;
  const displayValue = openTicketsCount; // Always show open tickets count
  const displaySubtitle = 'OPEN TICKETS';
  const isPositive = !hasOpenTickets; // Green if no open tickets (all resolved)

  // Pagination calculations
  const totalPages = Math.ceil(tickets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTickets = tickets.slice(startIndex, endIndex);

  // Reset to page 1 when modal opens
  useEffect(() => {
    if (showModal) {
      setCurrentPage(1);
    }
  }, [showModal]);

  return (
    <>
      <div className="bg-white border border-[#BBBBBB] rounded-lg p-4 min-w-[280px] md:min-w-0 md:flex-1 flex-shrink-0 overview-box h-[225px]" style={{ borderWidth: '1px' }}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between gap-2 mb-3 flex-shrink-0">
            <h3 className="text-sm-nova font-semibold text-black uppercase">Raise Tickets</h3>
            {!(isLoading || dataLoading) && (
              <Link
                to="/customer-service"
                className="px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded hover:bg-gray-800 transition-colors flex-shrink-0"
              >
                New ticket
              </Link>
            )}
          </div>
          {isLoading || dataLoading ? (
            <div className="flex items-center mt-2">
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm-nova text-gray-400 ml-2">Loading...</span>
            </div>
          ) : displayTicket ? (
            <div className="space-y-3 flex-1 min-h-0 overflow-y-auto">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Latest ticket</p>
              {/* Subject Section */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Subject</p>
                  <p className="text-xs font-semibold text-black leading-tight">
                    {formatDateMMDDYYYY(displayTicket.created_at)}
                  </p>
                </div>
                <div className="flex items-start gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-black leading-tight flex-1 min-w-0 break-words line-clamp-2">
                    {displayTicket.subject || 'No subject'}
                  </p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium flex-shrink-0 ${getStatusColor(displayTicket.current_ticket_info?.status || '')}`}>
                    {displayTicket.current_ticket_info?.status || 'Unknown'}
                  </span>
                </div>
              </div>

              {/* Description Section */}
              {displayTicket.description && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Description</p>
                  <p className="text-sm font-semibold text-black leading-tight flex-1 min-w-0 break-words line-clamp-2">
                    {displayTicket.description}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center flex-1 text-center py-4">
              <p className="text-sm text-gray-500">No tickets found</p>
              <p className="text-xs text-gray-400 mt-1">Create a support ticket to get started</p>
            </div>
          )}
        </div>
      </div>

    </>
  );
};

const OverviewSection: React.FC<OverviewSectionProps> = ({ dateRange }) => {
  const { userProfile } = useUser();
  const { currentArtistId, availableArtists, currentRoles, currentPermissions, hasPermission, loadingArtists } = useArtist();
  const isArtistMapped = userProfile?.artist?.[0]?.is_artist_mapped === true;
  const [overviewData, setOverviewData] = useState<ArtistOverviewData[]>([]);
  const [userArtists, setUserArtists] = useState<UserArtist[]>([]);
  const [currentArtist, setCurrentArtist] = useState<UserArtist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Check scroll position and update navigation buttons
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  // Scroll navigation functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -300,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 300,
        behavior: 'smooth'
      });
    }
  };


  // Update scroll position when data changes and add wheel event listener
  useEffect(() => {
    checkScrollPosition();
    const handleResize = () => checkScrollPosition();
    
    // Add native wheel event listener for better control
    const scrollContainer = scrollContainerRef.current;
    const handleNativeWheel = (e: WheelEvent) => {
      if (scrollContainer) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
        const canScrollHorizontally = scrollWidth > clientWidth;
        
        if (canScrollHorizontally && (e.deltaY !== 0 || e.deltaX !== 0)) {
          e.preventDefault();
          
          // Use deltaY for vertical mouse wheel, deltaX for horizontal scroll/trackpad
          const scrollAmount = e.deltaY !== 0 ? e.deltaY * 0.5 : e.deltaX * 0.5;
          
          // Direct scrollLeft manipulation for immediate response
          scrollContainer.scrollLeft += scrollAmount;
          
          // Update chevron states
          checkScrollPosition();
        }
      }
    };
    
    if (scrollContainer) {
      scrollContainer.addEventListener('wheel', handleNativeWheel, { passive: false });
    }
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('wheel', handleNativeWheel);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [overviewData]);

  // Load user's artists and overview data when artist changes
  useEffect(() => {
    const loadData = async () => {
      // Don't load if artists are still loading or no artist is selected
      if (loadingArtists) {
        setLoading(true);
        return;
      }
      
      if (!currentArtistId) {
        setLoading(false);
        setError(null);
        return;
      }

      // Verify localStorage has currentArtistId before making API calls
      const storedArtistId = localStorage.getItem('currentArtistId');
      if (!storedArtistId || storedArtistId !== currentArtistId.toString()) {
        // Wait a bit for ArtistContext to sync localStorage
        await new Promise(resolve => setTimeout(resolve, 100));
        const retryStoredArtistId = localStorage.getItem('currentArtistId');
        if (!retryStoredArtistId || retryStoredArtistId !== currentArtistId.toString()) {
          console.warn('[OverviewSection] currentArtistId not synced to localStorage yet, skipping API calls');
          setLoading(false);
          return;
        }
      }

      try {
        setLoading(true);
        setError(null);

        // Get user's artists first (for backward compatibility)
        const artistsResponse = await artistAnalyticsService.getUserArtists();
        setUserArtists(artistsResponse.artists);
        
        // Find the current artist from availableArtists or userArtists
        const selectedArtist = availableArtists.find(a => a.artistId === currentArtistId);
        const userArtist = artistsResponse.artists.find(artist => artist.id === currentArtistId?.toString());
        
        if (userArtist) {
          setCurrentArtist(userArtist);
        } else if (selectedArtist) {
          // Create a UserArtist-like object from ArtistInfo
          // Determine role from currentRoles or default to 'collaborator'
          const role = currentRoles && currentRoles.length > 0 
            ? (currentRoles[0] as 'owner' | 'manager' | 'collaborator') || 'collaborator'
            : (selectedArtist.isOwner ? 'owner' : 'collaborator');
          
          const artistObj: UserArtist = {
            id: selectedArtist.artistId.toString(),
            name: selectedArtist.artistName,
            role: role,
            isPrimary: selectedArtist.isOwner,
            permissions: currentPermissions || [],
            isDefault: false
          };
          setCurrentArtist(artistObj);
        }
        
        // Get overview data for current artist - all time data for total followers
        if (currentArtistId) {
          // Don't pass date filters to get all-time follower/subscriber counts
          const overviewResponse = await artistAnalyticsService.getArtistOverview(currentArtistId.toString());
          setOverviewData(overviewResponse.data);
        }

      } catch (err: any) {
        // Handle missing artist ID errors specifically
        if (err?.isMissingArtistId || err?.message?.includes('Missing required header') || err?.message?.includes('x-artist-id')) {
          setError('Please select an artist to view analytics data.');
          setOverviewData([]);
        } else {
          setError(err.message || 'Failed to load analytics data');
          
          // Fallback to mock data on error (only for non-missing-artist errors)
          setOverviewData([
            { platform: 'spotify', title: 'Spotify', subtitle: 'TOTAL STREAMS', currentStreams: 0, previousStreams: 0, percentage: '0.0%', isPositive: true },
            { platform: 'apple_music', title: 'Apple Music', subtitle: 'TOTAL STREAMS', currentStreams: 0, previousStreams: 0, percentage: '0.0%', isPositive: true },
            { platform: 'youtube', title: 'YouTube', subtitle: 'TOTAL STREAMS', currentStreams: 0, previousStreams: 0, percentage: '0.0%', isPositive: true },
            { platform: 'soundcloud', title: 'SoundCloud', subtitle: 'TOTAL STREAMS', currentStreams: 0, previousStreams: 0, percentage: '0.0%', isPositive: true },
          ]);
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentArtistId, availableArtists, loadingArtists]); // Refetch when artist changes or artists finish loading

  // Handle artist change
  const handleArtistChange = async (artistId: string) => {
    const selectedArtist = userArtists.find(artist => artist.id === artistId);
    if (!selectedArtist) return;

    try {
      setCurrentArtist(selectedArtist);
      setLoading(true);
      
      // Get all-time data for total followers calculation
      const overviewResponse = await artistAnalyticsService.getArtistOverview(artistId);
      setOverviewData(overviewResponse.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load artist data');
    } finally {
      setLoading(false);
    }
  };

  const canViewAccountBalance = hasPermission("banking:view") || hasPermission("banking:manage");

  if (loading && overviewData.length === 0) {
    return (
      <div className="mb-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-black">Today</h1>
            <p className="text-sm text-gray-600 mt-1">Change over last 30 days</p>
          </div>
          {/* Empty space for chevrons during loading */}
          <div></div>
        </div>
        <div className="flex gap-4 overflow-hidden">
          {/* Bank widget - always show when permission */}
          {canViewAccountBalance && (
             <AvailableBalanceTile artistId={currentArtist?.id} isLoading={loading} /> 
          )}
          {/* Metric widgets - only when artist is mapped */}
          {isArtistMapped && (
            <>
              <TotalFollowersTile overviewData={[]} isLoading={true} />
              <SpotifyListenersTile artistId={currentArtist?.id} isLoading={true} />
              <YouTubeMusicViewsTile artistId={currentArtist?.id} isLoading={true} />
            </>
          )}
          {/* Tickets widget - always show */}
          <CustomerSupportTile artistId={currentArtist?.id} isLoading={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-black">Today</h1>
          <p className="text-sm text-gray-600 mt-1">Change over last 30 days</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Navigation Chevrons - Spotify style positioning, hidden on mobile */}
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={scrollLeft}
              disabled={!canScrollLeft}
              className={`p-2 rounded-full transition-all duration-200 ${
                canScrollLeft 
                  ? 'bg-[#1C1C1C] hover:bg-[#2C2C2C] text-white shadow-lg' 
                  : 'bg-[#0F0F0F] text-white cursor-not-allowed'
              }`}
              title="Scroll left"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={scrollRight}
              disabled={!canScrollRight}
              className={`p-2 rounded-full transition-all duration-200 ${
                canScrollRight 
                  ? 'bg-[#1C1C1C] hover:bg-[#2C2C2C] text-white shadow-lg' 
                  : 'bg-[#0F0F0F] text-white cursor-not-allowed'
              }`}
              title="Scroll right"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          
          {/* Artist Switcher - Only show if user has multiple artists */}
          {userArtists.length > 1 && currentArtist && (
            <div className="flex items-center gap-3">
              <select 
                value={currentArtist.id}
                onChange={(e) => handleArtistChange(e.target.value)}
                className="bg-white border border-[#BBBBBB] text-black px-3 py-2 rounded text-sm"
              >
                {userArtists.map(artist => (
                  <option key={artist.id} value={artist.id}>
                    {artist.name}
                  </option>
                ))}
              </select>
              <span className={`px-2 py-1 rounded text-xs ${artistAnalyticsService.getRoleBadgeColor(currentArtist.role)}`}>
                {artistAnalyticsService.getRoleDisplayName(currentArtist.role)}
              </span>
            </div>
          )}
        </div>
      </div>
      
      {/* Error message moved outside header row */}
      {error && (
        <div className="mb-4">
          <p className="text-sm text-red-400">⚠️ {error}</p>
        </div>
      )}
      
      {/* Horizontal Scrollable Container */}
      <div className="relative">
        <div 
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide overview-scroll-container"
          onScroll={checkScrollPosition}
        >
          {canViewAccountBalance && (
            <AvailableBalanceTile artistId={currentArtist?.id} isLoading={loading} /> 
          )}
          {isArtistMapped && (
            <>
              <TotalFollowersTile overviewData={overviewData} isLoading={loading} />
              <SpotifyListenersTile artistId={currentArtist?.id} isLoading={loading} />
              <YouTubeMusicViewsTile artistId={currentArtist?.id} isLoading={loading} />
            </>
          )}
          <CustomerSupportTile artistId={currentArtist?.id} isLoading={loading} />
        </div>
      </div>
      
      {loading && (
        <div className="text-center mt-4">
          <p className="text-gray-400 text-sm">Updating analytics...</p>
        </div>
      )}
    </div>
  );
};

export default OverviewSection;
